import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../contexts/PaymentContext';
import StripeCheckout from './StripeCheckout';
import PartnerPlanWall from './PartnerPlanWall';
import { DOCUMENT } from '../constants/documentTheme';
import {
  PARTNER_DEVELOPER_API_MONTHLY,
  PARTNER_DEVELOPER_API_TIER_TYPE,
} from '../config/plans';

const btnSx = {
  borderRadius: 0,
  textTransform: 'none',
  bgcolor: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  px: 2,
  py: 1,
  '&:hover': { bgcolor: DOCUMENT.oxblood },
};

const outlineBtnSx = {
  borderRadius: 0,
  textTransform: 'none',
  borderColor: DOCUMENT.ruleHard,
  color: DOCUMENT.ink,
};

/**
 * When the partner lacks sandbox or production API, show upgrade / add-on CTAs
 * instead of a dead-end error.
 */
const DeveloperAccessCallout = ({
  entitlements,
  onEntitlementsChange,
  compact = false,
  errorMessage = null,
}) => {
  const { t } = useTranslation();
  const { createCheckoutSession, clearError } = usePayment();
  const [wallOpen, setWallOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const features = entitlements?.features || {};
  const hasSandbox = Boolean(features.hasApiSandbox);
  const hasProduction = Boolean(features.hasProductionApi);
  const needsUpgrade = !hasSandbox || !hasProduction;

  if (!needsUpgrade && !errorMessage) return null;

  const handleAddonCheckout = async () => {
    try {
      setBusy(true);
      setLocalError('');
      clearError();
      const sessionData = await createCheckoutSession({
        tripId: null,
        tierType: PARTNER_DEVELOPER_API_TIER_TYPE,
        usePartnerAuth: true,
      });
      if (sessionData?.clientSecret) {
        setCheckoutData(sessionData);
        setCheckoutOpen(true);
        return;
      }
      if (sessionData?.success) {
        onEntitlementsChange?.();
      }
    } catch (err) {
      setLocalError(
        err.response?.data?.error
        || err.response?.data?.message
        || t('developerPortal.upgrade.checkoutFailed')
      );
    } finally {
      setBusy(false);
    }
  };

  const handleWallSuccess = () => {
    setWallOpen(false);
    onEntitlementsChange?.();
  };

  const handlePaymentSuccess = () => {
    setCheckoutOpen(false);
    setCheckoutData(null);
    onEntitlementsChange?.();
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: compact ? 2 : { xs: 2.5, md: 3 },
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.ruleHard}`,
      }}
    >
      {!hasSandbox ? (
        <>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: compact ? 18 : 22, mb: 0.75 }}>
            {t('developerPortal.upgrade.needAgencyTitle')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
            {t('developerPortal.upgrade.needAgencyBody')}
          </Typography>
        </>
      ) : !hasProduction ? (
        <>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: compact ? 18 : 22, mb: 0.75 }}>
            {t('developerPortal.upgrade.needAddonTitle', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '62ch' }}>
            {t('developerPortal.upgrade.needAddonBody')}
          </Typography>
        </>
      ) : null}

      {errorMessage ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {typeof errorMessage === 'string' ? errorMessage : String(errorMessage)}
        </Alert>
      ) : null}
      {localError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{localError}</Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {!hasSandbox ? (
          <Button
            variant="contained"
            disableElevation
            onClick={() => setWallOpen(true)}
            sx={btnSx}
          >
            {t('developerPortal.upgrade.choosePlan')}
          </Button>
        ) : null}
        {hasSandbox && !hasProduction ? (
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={handleAddonCheckout}
            sx={btnSx}
          >
            {busy
              ? t('developerPortal.upgrade.startingCheckout')
              : t('developerPortal.upgrade.addDeveloperApi', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
          </Button>
        ) : null}
        <Button
          variant="outlined"
          onClick={() => setWallOpen(true)}
          sx={outlineBtnSx}
        >
          {t('developerPortal.upgrade.viewPlans')}
        </Button>
      </Box>

      <PartnerPlanWall
        open={wallOpen}
        onClose={() => setWallOpen(false)}
        onSuccess={handleWallSuccess}
        entitlements={entitlements}
        reason="billing"
      />

      {checkoutOpen && checkoutData?.clientSecret ? (
        <StripeCheckout
          open={checkoutOpen}
          onClose={() => {
            setCheckoutOpen(false);
            setCheckoutData(null);
          }}
          clientSecret={checkoutData.clientSecret}
          tripTitle={t('developerPortal.upgrade.checkoutTitle')}
          upgradeType={PARTNER_DEVELOPER_API_TIER_TYPE}
          onSuccess={handlePaymentSuccess}
        />
      ) : null}
    </Box>
  );
};

export default DeveloperAccessCallout;
